import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { HypeKind } from '@racksavant/core';
import { MOODS } from '@racksavant/core';
import { cx } from '../cx';
import type { PlateId } from '../photos';
import { Face, Plate } from './Photo';
import s from './DeviceDeck.module.css';

/* =========================================================================
   The device deck.

   Clips of the real app, framed in real devices, dealt like a hand of cards.

   On corners: the frames here are ROUND, and that is not a contradiction of
   the square-corner rule. That rule governs interface chrome — our buttons,
   our cards, our panels. These are depictions of physical objects. A phone
   bezel has a 44px radius because the object does; drawing it square would be
   drawing it wrong.

   On the clips: `scripts/record-demos.mjs` drives the deployed app with
   Playwright and writes a `.webm` and a `.jpg` poster per surface into
   `packages/ui/assets/demos/`. That runs in CI, so on a fresh checkout there
   is nothing there at all. The deck therefore degrades in three steps —
   video, then poster, then a screen composed here out of the same editorial
   photography the rest of the site uses. All three look finished; today's
   real state is the third one.
   ========================================================================= */

/* Resilient by construction: a glob over a directory that does not exist
   resolves to an empty record rather than failing the build. */
const CLIPS = import.meta.glob('../../../../../packages/ui/assets/demos/*.webm', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const POSTERS = import.meta.glob('../../../../../packages/ui/assets/demos/*.jpg', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

function findAsset(map: Record<string, string>, id: string, ext: string): string | undefined {
  const suffix = `/${id}.${ext}`;
  const key = Object.keys(map).find((path) => path.endsWith(suffix));
  return key ? map[key] : undefined;
}

/* ------------------------------------------------------------------ deck */

export type DemoId =
  | 'rack-mobile'
  | 'studio-mobile'
  | 'develop-mobile'
  | 'look-desktop'
  | 'room-desktop'
  | 'runway-desktop';

type MockLayout = 'grid' | 'single' | 'split';

interface Demo {
  id: DemoId;
  device: 'phone' | 'desktop';
  /** The running head shown under the card. */
  title: string;
  /** What the clip shows, for anyone who cannot watch it. */
  label: string;
  layout: MockLayout;
  plates: PlateId[];
  /** The line burned into the mock screen. */
  screenLine: string;
  faces?: HypeKind[];
}

const DEMOS: Record<DemoId, Demo> = {
  'rack-mobile': {
    id: 'rack-mobile',
    device: 'phone',
    title: 'The rack',
    label: 'Screen recording: browsing the rack by mood on a phone',
    layout: 'grid',
    plates: ['hero', 'detail', 'street', 'duo'],
    screenLine: 'Regal · 214 pieces',
  },
  'studio-mobile': {
    id: 'studio-mobile',
    device: 'phone',
    title: 'The studio',
    label: 'Screen recording: composing a look in the studio',
    layout: 'single',
    plates: ['mirror'],
    screenLine: 'Three garments · moody, regal',
  },
  'develop-mobile': {
    id: 'develop-mobile',
    device: 'phone',
    title: 'Developing',
    label: 'Screen recording: a look developing',
    layout: 'single',
    plates: ['hero'],
    screenLine: 'Setting the lights…',
  },
  'look-desktop': {
    id: 'look-desktop',
    device: 'desktop',
    title: 'A finished look',
    label: 'Screen recording: a finished look and its mood read',
    layout: 'split',
    plates: ['guyStreet'],
    screenLine: 'The jacket is doing the talking. Let it.',
  },
  'room-desktop': {
    id: 'room-desktop',
    device: 'desktop',
    title: 'Fitting room',
    label: 'Screen recording: a live fitting room with four people reacting',
    layout: 'single',
    plates: ['group'],
    screenLine: 'Room · MIRRORBALL · live',
    faces: ['obsessed', 'menace', 'iconic', 'crying'],
  },
  'runway-desktop': {
    id: 'runway-desktop',
    device: 'desktop',
    title: 'The Runway',
    label: 'Screen recording: the public Runway feed',
    layout: 'grid',
    plates: ['guy', 'duo', 'street'],
    screenLine: 'Following',
  },
};

export const DEFAULT_DECK: DemoId[] = [
  'look-desktop',
  'rack-mobile',
  'room-desktop',
  'studio-mobile',
  'runway-desktop',
  'develop-mobile',
];

/* ------------------------------------------------------------------ mock */

const CHIPS = [MOODS.regal, MOODS.moody, MOODS.feral, MOODS.minimal];

/**
 * The screen we compose ourselves when no recording exists. It is built from
 * the same photography as the rest of the site, so a deck with zero video
 * files still shows real clothes on real people.
 */
function Mock({ demo }: { demo: Demo }) {
  return (
    <div className={cx(s.mock, s[`mock_${demo.layout}`])}>
      <div className={s.mockBar}>
        <span className={s.mockBrand}>RackSavant</span>
        <span className={s.mockChips}>
          {CHIPS.map((mood, i) => (
            <span
              key={mood.id}
              className={cx(s.mockChip, i === 0 && s.mockChipOn)}
              style={{ '--c': mood.color } as CSSProperties}
            >
              {mood.label}
            </span>
          ))}
        </span>
      </div>

      <div className={s.mockBody}>
        {demo.layout === 'grid' ? (
          <div className={s.mockGrid}>
            {demo.plates.map((id) => (
              <Plate key={id} id={id} caption={false} ratio={2 / 3} />
            ))}
          </div>
        ) : null}

        {demo.layout === 'single' ? (
          <div className={s.mockSingle}>
            <Plate id={demo.plates[0]} caption={false} ratio={demo.device === 'phone' ? 3 / 4 : 16 / 9} />
            {demo.faces ? (
              <div className={s.mockFaces}>
                {demo.faces.map((kind, i) => (
                  <Face key={kind} kind={kind} size={44} tilt={i % 2 ? 5 : -5} />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {demo.layout === 'split' ? (
          <div className={s.mockSplit}>
            <Plate id={demo.plates[0]} caption={false} ratio={3 / 4} />
            <div className={s.mockRead}>
              <span className={s.mockReadLabel}>The mood read</span>
              <p className={s.mockVerdict}>{demo.screenLine}</p>
              <span className={s.mockMeter}>
                <span className={s.mockMeterFill} />
              </span>
              <p className={s.mockAdjectives}>tailored / unhurried / faintly villainous</p>
            </div>
          </div>
        ) : null}
      </div>

      {demo.layout !== 'split' ? <p className={s.mockLine}>{demo.screenLine}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ card */

interface CardProps {
  demo: Demo;
  /** Distance from the front of the deck. 0 is the card being dealt. */
  depth: number;
  total: number;
  part: number;
  active: boolean;
  reduced: boolean;
  drag: { x: number; settling: boolean } | null;
  registerVideo: (id: DemoId, node: HTMLVideoElement | null) => void;
  onHover: (depth: number | null) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}

function Card({
  demo,
  depth,
  total,
  part,
  active,
  reduced,
  drag,
  registerVideo,
  onHover,
  onPointerDown,
}: CardProps) {
  const clip = findAsset(CLIPS, demo.id, 'webm');
  const poster = findAsset(POSTERS, demo.id, 'jpg');

  const style = {
    '--depth': depth,
    '--part': `${part}px`,
    '--drag-x': drag ? `${drag.x}px` : '0px',
    '--drag-r': drag ? `${drag.x / 26}deg` : '0deg',
    zIndex: total - depth,
  } as CSSProperties;

  return (
    <article
      className={cx(s.card, s[demo.device], active && s.cardActive, drag?.settling && s.settling)}
      style={style}
      data-depth={depth}
      tabIndex={active ? 0 : -1}
      aria-current={active ? 'true' : undefined}
      aria-label={demo.label}
      onMouseEnter={() => onHover(depth)}
      onMouseLeave={() => onHover(null)}
      onPointerDown={active ? onPointerDown : undefined}
    >
      <div className={s.frame}>
        {demo.device === 'phone' ? (
          <span className={s.notch} aria-hidden="true" />
        ) : (
          <span className={s.chrome} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        )}

        <div className={s.screen}>
          {clip ? (
            <video
              ref={(node) => registerVideo(demo.id, node)}
              className={s.video}
              src={clip}
              poster={poster}
              aria-label={demo.label}
              muted
              loop
              playsInline
              preload="metadata"
              autoPlay={!reduced && depth < 2}
            />
          ) : poster ? (
            <img className={s.video} src={poster} alt={demo.label} loading="lazy" decoding="async" />
          ) : (
            <Mock demo={demo} />
          )}
        </div>

        <span className={s.sheen} aria-hidden="true" />
      </div>

      <p className={s.cardTitle}>
        <span>{demo.title}</span>
        <span className={s.cardDevice}>{demo.device === 'phone' ? 'iPhone' : 'Desktop'}</span>
      </p>
    </article>
  );
}

/* ------------------------------------------------------------------ deck */

export interface DeviceDeckProps {
  ids?: DemoId[];
  className?: string;
  /** `pair` is the tighter two-card arrangement used on /features. */
  variant?: 'deck' | 'pair';
}

function usePrefersReduced(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduced;
}

export function DeviceDeck({ ids = DEFAULT_DECK, className, variant = 'deck' }: DeviceDeckProps) {
  const reduced = usePrefersReduced();
  const deckRef = useRef<HTMLDivElement | null>(null);
  const videos = useRef(new Map<DemoId, HTMLVideoElement>());

  const [order, setOrder] = useState<DemoId[]>(ids);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ x: number; settling: boolean } | null>(null);
  const [visible, setVisible] = useState(true);

  // Keyed on contents, not on identity: a caller passing an inline array must
  // not be able to put this into a render loop.
  const idsKey = ids.join('|');
  useEffect(() => {
    setOrder(idsKey.split('|') as DemoId[]);
  }, [idsKey]);

  const cycle = useCallback((direction: 1 | -1) => {
    setOrder((current) => {
      if (current.length < 2) return current;
      const next = current.slice();
      if (direction === 1) next.push(next.shift() as DemoId);
      else next.unshift(next.pop() as DemoId);
      return next;
    });
  }, []);

  /* ---- parallax: the whole stack leans toward the pointer, gently ---- */
  useEffect(() => {
    if (reduced) return;
    const deck = deckRef.current;
    if (!deck) return;

    const onMove = (event: PointerEvent) => {
      const box = deck.getBoundingClientRect();
      const nx = (event.clientX - box.left) / box.width - 0.5;
      const ny = (event.clientY - box.top) / box.height - 0.5;
      setTilt({ x: Math.max(-8, Math.min(8, -ny * 14)), y: Math.max(-8, Math.min(8, nx * 14)) });
    };
    const onLeave = () => setTilt({ x: 0, y: 0 });

    deck.addEventListener('pointermove', onMove);
    deck.addEventListener('pointerleave', onLeave);
    return () => {
      deck.removeEventListener('pointermove', onMove);
      deck.removeEventListener('pointerleave', onLeave);
    };
  }, [reduced]);

  /* ---- drag the top card, and shuffle it to the back past the threshold ---- */
  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (reduced || order.length < 2) return;
      const node = event.currentTarget;
      const startX = event.clientX;
      node.setPointerCapture?.(event.pointerId);
      setDrag({ x: 0, settling: false });

      const move = (moveEvent: PointerEvent) => {
        setDrag({ x: moveEvent.clientX - startX, settling: false });
      };

      const end = (endEvent: PointerEvent) => {
        node.releasePointerCapture?.(endEvent.pointerId);
        node.removeEventListener('pointermove', move);
        node.removeEventListener('pointerup', end);
        node.removeEventListener('pointercancel', end);
        const travelled = endEvent.clientX - startX;
        // Let go of it and it carries on, then settles: either back into the
        // deck, or off the front and around to the back.
        setDrag({ x: Math.sign(travelled) * (Math.abs(travelled) > 120 ? 460 : 0), settling: true });
        window.setTimeout(() => {
          if (Math.abs(travelled) > 120) cycle(travelled < 0 ? 1 : -1);
          setDrag(null);
        }, 320);
      };

      node.addEventListener('pointermove', move);
      node.addEventListener('pointerup', end);
      node.addEventListener('pointercancel', end);
    },
    [cycle, order.length, reduced],
  );

  /* ---- only the front two play; nothing plays off-screen or in a hidden tab ---- */
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setVisible(entry.isIntersecting);
      },
      { rootMargin: '80px' },
    );
    observer.observe(deck);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sync = () => {
      const hidden = typeof document !== 'undefined' && document.hidden;
      order.forEach((id, depth) => {
        const node = videos.current.get(id);
        if (!node) return;
        const shouldPlay = !reduced && visible && !hidden && depth < 2;
        if (shouldPlay) void node.play().catch(() => undefined);
        else node.pause();
      });
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [order, reduced, visible]);

  const registerVideo = useCallback((id: DemoId, node: HTMLVideoElement | null) => {
    if (node) videos.current.set(id, node);
    else videos.current.delete(id);
  }, []);

  const stageStyle = useMemo(
    () =>
      ({
        '--tilt-x': `${tilt.x}deg`,
        '--tilt-y': `${tilt.y}deg`,
      }) as CSSProperties,
    [tilt],
  );

  const front = order[0];
  if (!front) return null;

  return (
    <div
      ref={deckRef}
      className={cx(s.deck, s[variant], reduced && s.static, className)}
      role="group"
      aria-roledescription="Deck of app recordings"
      aria-label="RackSavant running on a phone and on the desktop"
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          cycle(1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          cycle(-1);
        }
      }}
    >
      <div className={s.stage} style={stageStyle}>
        {order.map((id, depth) => (
          <Card
            key={id}
            demo={DEMOS[id]}
            depth={depth}
            total={order.length}
            part={hovered !== null && depth > hovered ? 44 : 0}
            active={depth === 0}
            reduced={reduced}
            drag={depth === 0 ? drag : null}
            registerVideo={registerVideo}
            onHover={setHovered}
            onPointerDown={onPointerDown}
          />
        ))}
      </div>

      <div className={s.controls}>
        <button
          type="button"
          className={s.control}
          aria-label="Previous clip"
          onClick={() => cycle(-1)}
        >
          <span aria-hidden="true">←</span>
          <span className={s.controlLabel} aria-hidden="true">
            Previous
          </span>
        </button>
        <p className={s.counter} aria-live="polite">
          {DEMOS[front].title}
          <span aria-hidden="true"> · </span>
          <span className={s.counterIndex}>
            {ids.indexOf(front) + 1}/{order.length}
          </span>
        </p>
        <button
          type="button"
          className={s.control}
          aria-label="Next clip"
          onClick={() => cycle(1)}
        >
          <span className={s.controlLabel} aria-hidden="true">
            Next
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
}
